import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../cn';
import { SpinnerIcon } from '../icons';

/**
 * Graphite Desk button. One primary per view, placed last in its group.
 * - primary: graphite solid
 * - secondary: white with strong border (default)
 * - ghost: text-only, for tertiary actions and toolbars
 * - danger: red outline; danger-solid only inside a confirmation
 * - link: inline text action
 */
export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-sm border font-medium whitespace-nowrap select-none transition-colors duration-(--duration-fade) disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'border-primary bg-primary text-primary-foreground hover:border-primary-hover hover:bg-primary-hover',
        secondary: 'border-border-strong bg-surface text-ink hover:bg-surface-muted',
        ghost: 'border-transparent bg-transparent text-ink-secondary hover:bg-surface-muted hover:text-ink',
        danger: 'border-negative/40 bg-surface text-negative hover:bg-negative-subtle',
        'danger-solid': 'border-negative bg-negative text-primary-foreground hover:opacity-90',
        link: 'h-auto border-transparent bg-transparent p-0 text-accent underline-offset-3 hover:text-accent-hover hover:underline',
      },
      size: {
        xs: 'h-6 px-2 text-meta',
        sm: 'h-7 px-2.5 text-label',
        md: 'h-8 px-3 text-body',
        lg: 'h-9 px-4 text-body',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0' }],
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner, keeps the label, and blocks repeated clicks. */
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, icon, children, disabled, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <SpinnerIcon className="animate-spin" size={14} /> : icon}
          {children}
        </>
      )}
    </Comp>
  );
});
